// Virtual filesystem for the Career CLI.
//
// Built lazily from `resumeData` so we never duplicate the source of truth.
// File contents are generated by template functions on demand (cat) so the
// initial bundle stays small and any edits to `resumeData` flow through.
//
// Layout:
//   ~/
//   ├── README.md
//   └── career/
//       ├── about/{bio,contact}.md
//       ├── experience/<slug>.md   (one per role)
//       ├── skills/<slug>.md       (one per category)
//       ├── education/<slug>.md
//       ├── certifications.md
//       └── resume.pdf             (binary flag — v2 wires download)
//
// Path semantics roughly match POSIX:
//   - `~` is the home dir (`/home/thanh.nguyen`).
//   - `cd` with no args returns home.
//   - `..` walks up; `.` is no-op.
//   - Absolute paths start with `/` or `~`.

import { resumeData } from '../../data/resumeData';
import type { Experience, Skill, Education, Certification } from '../../types/resume.types';

export const HOME = '/home/thanh.nguyen';
export const INITIAL_CWD = '/home/thanh.nguyen/career';

/** Marker for binary files — `cat` refuses to print them as text. */
export const BINARY_MARKER = '__BINARY__';

// ---------------------------------------------------------------------------
// Slugification (must be stable: the same input always yields the same slug)
// ---------------------------------------------------------------------------

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Slugs for experience entries are derived from the company name, with
 * disambiguation suffixes for repeated companies (e.g. two Terralogic roles).
 * The result is deterministic and matches the spec's example filenames.
 */
function experienceSlug(exp: Experience): string {
  const company = slugify(exp.company.replace(/\(.*\)$/, '').trim());
  // Special-case the two Terralogic entries called out in the spec.
  if (company === 'terralogic') {
    if (/lead/i.test(exp.position)) return 'terralogic-lead';
    if (/sr|senior/i.test(exp.position)) return 'terralogic-sr-qc';
  }
  // Strip parenthetical site notes (e.g. "Cisco (On-site)" -> "cisco").
  if (company === 'cisco-on-site') return 'cisco';
  if (company === 'cisco') return 'cisco';
  if (company === 'tma-solutions') return 'tma';
  if (company === 'imt-solutions') return 'imt';
  if (company === 'ag-trade-investment-promotion-center') return 'ag-trade';
  if (company === 'donga-bank-head-office') return 'donga-bank';
  return company;
}

function skillSlug(skill: Skill): string {
  const c = skill.category.toLowerCase();
  if (c.includes('language')) return 'languages';
  if (c.includes('automation')) return 'automation-frameworks';
  if (c.includes('api')) return 'api-testing';
  if (c.includes('performance')) return 'performance';
  if (c.includes('ci/cd') || c.includes('cicd') || c.includes('devops')) return 'cicd-devops';
  if (c.includes('analysis') || c.includes('debug')) return 'analysis-debugging';
  if (c.includes('methodolog')) return 'methodologies';
  if (c.includes('ai') || c.includes('emerging')) return 'ai-emerging';
  if (c.includes('project')) return 'project-management';
  return slugify(skill.category);
}

function educationSlug(edu: Education): string {
  const i = edu.institution.toLowerCase();
  if (i.includes('information technology')) return 'uit';
  if (i.includes('economics')) return 'uel';
  return slugify(edu.institution);
}

// ---------------------------------------------------------------------------
// Content templates
// ---------------------------------------------------------------------------

function readmeContent(): string {
  const p = resumeData.personalInfo;
  return [
    `# Welcome — ${p.name}`,
    '',
    `${p.title} based in ${p.location}.`,
    '',
    "This site is a faux career terminal. Everything is a file.",
    '',
    'Quick start:',
    '  ls                     list current directory',
    '  cat about/bio.md       read the long-form bio',
    '  cd experience          step into the experience folder',
    '  tree                   see the whole map',
    '  help                   list every command',
    '',
    "Prefer a traditional layout? Hit the [Classic view →] link in the top-right.",
  ].join('\n');
}

function bioContent(): string {
  return [
    '# About',
    '',
    resumeData.about,
  ].join('\n');
}

function contactContent(): string {
  const p = resumeData.personalInfo;
  const lines = [
    '# Contact',
    '',
    `email:    ${p.email}`,
    `location: ${p.location}`,
  ];
  if (p.linkedin) lines.push(`linkedin: ${p.linkedin}`);
  if (p.github) lines.push(`github:   ${p.github}`);
  if (p.phone) lines.push(`phone:    ${p.phone}`);
  return lines.join('\n');
}

function experienceContent(exp: Experience): string {
  const out: string[] = [];
  out.push(`# ${exp.position} — ${exp.company}`);
  out.push('');
  out.push(`Duration: ${exp.duration}`);
  if (exp.type === 'current') out.push('Status:   current');
  out.push('');
  if (exp.responsibilities.length > 0) {
    out.push('## Responsibilities');
    out.push('');
    for (const r of exp.responsibilities) out.push(`  - ${r}`);
    out.push('');
  }
  if (exp.achievements && exp.achievements.length > 0) {
    out.push('## Achievements');
    out.push('');
    for (const a of exp.achievements) out.push(`  - ${a}`);
    out.push('');
  }
  if (exp.projects && exp.projects.length > 0) {
    out.push('## Projects');
    out.push('');
    for (const p of exp.projects) {
      out.push(`  ${p.title}`);
      for (const d of p.description) out.push(`    - ${d}`);
      out.push('');
    }
  }
  return out.join('\n').trimEnd();
}

function skillContent(skill: Skill): string {
  const out = [
    `# ${skill.category}`,
    '',
  ];
  for (const item of skill.items) out.push(`  - ${item}`);
  return out.join('\n');
}

function educationContent(edu: Education): string {
  const out = [
    `# ${edu.institution}`,
    '',
    `Degree:   ${edu.degree}`,
  ];
  if (edu.field) out.push(`Field:    ${edu.field}`);
  out.push(`Duration: ${edu.duration}`);
  if (edu.gpa) out.push(`GPA:      ${edu.gpa}`);
  return out.join('\n');
}

function certificationsContent(): string {
  const out = ['# Certifications & Awards', ''];
  const groups: { title: string; items: Certification[] } = {
    title: 'all',
    items: resumeData.certifications,
  };
  for (const c of groups.items) {
    out.push(`  - ${c.name}`);
    if (c.issuer) out.push(`      issuer: ${c.issuer}`);
    if (c.date) out.push(`      date:   ${c.date}`);
    if (c.url) out.push(`      url:    ${c.url}`);
  }
  return out.join('\n');
}

// ---------------------------------------------------------------------------
// Tree construction
// ---------------------------------------------------------------------------

interface FileNode {
  type: 'file';
  /** Generator avoids storing all content in memory until cat is called. */
  read: () => string;
  /** True for files that should refuse `cat` (e.g. resume.pdf). */
  binary?: boolean;
}

interface DirNode {
  type: 'dir';
  children: Record<string, Node>;
}

type Node = FileNode | DirNode;

function buildFilesystem(): DirNode {
  // Build experience/ children.
  const expChildren: Record<string, Node> = {};
  for (const exp of resumeData.experience) {
    const slug = experienceSlug(exp);
    expChildren[`${slug}.md`] = {
      type: 'file',
      read: () => experienceContent(exp),
    };
  }

  // Build skills/ children.
  const skillChildren: Record<string, Node> = {};
  for (const skill of resumeData.skills) {
    skillChildren[`${skillSlug(skill)}.md`] = {
      type: 'file',
      read: () => skillContent(skill),
    };
  }

  // Build education/ children.
  const eduChildren: Record<string, Node> = {};
  for (const edu of resumeData.education) {
    eduChildren[`${educationSlug(edu)}.md`] = {
      type: 'file',
      read: () => educationContent(edu),
    };
  }

  // Inner contents that live under /home/thanh.nguyen/
  const homeContents: Record<string, Node> = {
    'README.md': { type: 'file', read: readmeContent },
    career: {
      type: 'dir',
      children: {
        about: {
          type: 'dir',
          children: {
            'bio.md': { type: 'file', read: bioContent },
            'contact.md': { type: 'file', read: contactContent },
          },
        },
        experience: { type: 'dir', children: expChildren },
        skills: { type: 'dir', children: skillChildren },
        education: { type: 'dir', children: eduChildren },
        'certifications.md': { type: 'file', read: certificationsContent },
        'resume.pdf': { type: 'file', read: () => BINARY_MARKER, binary: true },
      },
    },
  };

  // Full POSIX-style root: / → home → thanh.nguyen → (real content)
  // This matches HOME = '/home/thanh.nguyen' so resolvePath/nodeAt agree.
  return {
    type: 'dir',
    children: {
      home: {
        type: 'dir',
        children: {
          'thanh.nguyen': { type: 'dir', children: homeContents },
        },
      },
    },
  };
}

// Single root rebuilt on module import.
const root = buildFilesystem();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Normalize a path relative to `cwd` into an absolute, segment-array form.
 * Returns null if the path escapes the root or hits an empty segment.
 *
 * Supports:
 *   - `~`        -> home
 *   - `/abs`     -> absolute
 *   - `relative` -> joined to cwd
 *   - `..`/`.`   -> walked
 *   - trailing/leading slashes are tolerated
 */
export function resolvePath(input: string, cwd: string): string | null {
  if (!input || input.trim() === '') return cwd;

  let raw = input.trim();
  // Expand `~` (must be at start; either bare `~` or `~/foo`).
  if (raw === '~') raw = HOME;
  else if (raw.startsWith('~/')) raw = HOME + raw.slice(1);

  // Start from cwd unless absolute.
  const base = raw.startsWith('/') ? [] : cwd.split('/').filter(Boolean);
  const segs = raw.split('/').filter(Boolean);

  const stack = [...base];
  for (const s of segs) {
    if (s === '.') continue;
    if (s === '..') {
      if (stack.length > 0) stack.pop();
      continue;
    }
    stack.push(s);
  }
  return '/' + stack.join('/');
}

/** Walk the tree to a node. Returns null if not found. */
function nodeAt(absPath: string): Node | null {
  if (absPath === '/' || absPath === '') return root;
  const parts = absPath.split('/').filter(Boolean);
  let cur: Node = root;
  for (const p of parts) {
    if (cur.type !== 'dir') return null;
    const child: Node | undefined = cur.children[p];
    if (!child) return null;
    cur = child;
  }
  return cur;
}

export function exists(absPath: string): boolean {
  return nodeAt(absPath) !== null;
}

export function isDir(absPath: string): boolean {
  const n = nodeAt(absPath);
  return n !== null && n.type === 'dir';
}

export function isFile(absPath: string): boolean {
  const n = nodeAt(absPath);
  return n !== null && n.type === 'file';
}

export function isBinary(absPath: string): boolean {
  const n = nodeAt(absPath);
  return n !== null && n.type === 'file' && n.binary === true;
}

export interface DirEntry {
  name: string;
  type: 'file' | 'dir';
}

/** List entries of a directory. Returns null if the path is not a dir. */
export function readDir(absPath: string): DirEntry[] | null {
  const n = nodeAt(absPath);
  if (!n || n.type !== 'dir') return null;
  return Object.entries(n.children)
    .map(([name, child]) => ({ name, type: child.type as 'file' | 'dir' }))
    .sort((a, b) => {
      // Directories first, then files; both alphabetical.
      if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

/** Read a file's text. Returns null for non-files; BINARY_MARKER for binaries. */
export function readFile(absPath: string): string | null {
  const n = nodeAt(absPath);
  if (!n || n.type !== 'file') return null;
  return n.read();
}

/**
 * Compress an absolute path to its display form, swapping the home prefix
 * for `~`. Used by the prompt and `pwd`.
 */
export function prettyPath(absPath: string): string {
  if (absPath === HOME) return '~';
  if (absPath.startsWith(HOME + '/')) return '~' + absPath.slice(HOME.length);
  return absPath;
}

/** Recursive listing for the `tree` command. */
export function tree(absPath: string, prefix = ''): string[] {
  const n = nodeAt(absPath);
  if (!n) return [];
  const out: string[] = [];
  if (prefix === '') {
    out.push(prettyPath(absPath));
  }
  if (n.type !== 'dir') return out;
  const entries = readDir(absPath) ?? [];
  entries.forEach((e, i) => {
    const isLast = i === entries.length - 1;
    const branch = isLast ? '└── ' : '├── ';
    out.push(prefix + branch + e.name);
    if (e.type === 'dir') {
      const extension = isLast ? '    ' : '│   ';
      out.push(...tree(absPath + '/' + e.name, prefix + extension));
    }
  });
  return out;
}
